import { ChangeDetectorRef, Component } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UtilitiesService } from '../../services/utilities.service';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { GlobalVariables } from '../../constants/global-constants';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { GlobalMenuComponent } from '../global-menu/global-menu.component';
import { MatDialog } from '@angular/material/dialog';
import { initPopupData } from '../../model/popup-data';
import { UserFeedbackConfirmationComponent } from '../user-feedback-confirmation/user-feedback-confirmation.component';
import { CreateNewClientService } from '../../services/onboarding-wizard/create-new-client.service';
import { AcquireOnboardingWizardService } from '../../services/acquire-onboarding-wizard/acquire-onboarding-wizard.service';


@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    LanguageSelectorComponent,
    ProfileMenuComponent,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    GlobalMenuComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  showlogo: boolean = false;
  isSelectClientRoute: boolean;
  companyName: string;
  languageSelectTooltip = GlobalVariables.headerClientTooltip;
  userEmail: string;
  isUniqueClient: boolean;
  clientId: number;
  isDarkMode: boolean;
  isFdUser: boolean;
  isOnboarding: boolean;
  isDemo: boolean;
  popup = initPopupData;


  constructor(public utilities: UtilitiesService,
    public router: Router,
    private dialog: MatDialog,
    private createNewClientService: CreateNewClientService,
    private createAcquirerOnboardingWizardService: AcquireOnboardingWizardService,
    private cdr: ChangeDetectorRef
  ) {
    this.utilities.displayLogoEvent.subscribe((show) => {
      this.showlogo = show;
      this.cdr.detectChanges();
    });

    this.utilities.clientContextRefresh$.subscribe(() => {
      this.syncHeaderContextFromUtilities();
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const isSelectClient = this.router.url === '/select-client' || this.router.url === '/contract-acknowledgment';
        const isOnboarding = this.router.url.includes('/client-onboarding');
        const isGlobal = this.router.url.includes('global');
        this.utilities.updateRouteStatus(isSelectClient);
        this.utilities.updateRouteOnboardingStatus(isOnboarding);
        this.utilities.updateRouteGlobalStatus(isGlobal);
        this.syncHeaderContextFromUtilities();
      });
  }

  /** Keeps toolbar client chip / flags aligned with UtilitiesService after navigation or context changes. */
  private syncHeaderContextFromUtilities(): void {
    this.isUniqueClient = this.utilities.isUniqueClientValue;
    this.companyName = this.utilities.companyNameValue;
    this.userEmail = this.utilities.userEmailValue;
    this.clientId = this.utilities.clientId;
    this.isFdUser = this.utilities.isFdUserValue;
    this.isOnboarding = this.utilities.companyStatusValue === 'Onboarding' ? true : false;
    this.checkRoute();
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.syncHeaderContextFromUtilities();
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'theme-dark') {
      this.isDarkMode = true;
      this.themeSelected('theme-dark');
    } else {
      this.isDarkMode = false;
      this.themeSelected('light-theme');
    }
  }

  checkRoute() {
    this.isSelectClientRoute = ((this.router.url === '/select-client') || (this.router.url === '/onboarding-in-process') || (this.router.url === '/contract-acknowledgment'));
  }

  reloadHeader(){
    this.cdr.detectChanges();
  }

  redirectSelectClient() {
    if (this.utilities.isUniqueClientValue) {
      return;
    }
    this.utilities.setIsUniqueClient(null);
    this.router.navigate(['select-client']);
  }

  themeSelected(theme: string) {
    if (theme === 'theme-dark') {
      document.body.classList.remove('light-theme');
      document.body.classList.add('theme-dark');
      this.isDarkMode = true;
    } else {
      document.body.classList.remove('theme-dark');
      document.body.classList.add('light-theme');
      this.isDarkMode = false;
    }

    localStorage.setItem('theme', theme);
  }

  toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    this.themeSelected(this.isDarkMode ? 'theme-dark' : 'light-theme');
  }

  createOnbClient() {
    const dialogRef = this.dialog.open(UserFeedbackConfirmationComponent);

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.data !== GlobalVariables.CONFIRMED) return;

      this.utilities.fullPageLoading = true;
      this.createNewClientService.createOnbClient().subscribe({
        next: (res: any) => {
          this.router.navigate(['client-onboarding', res.result.fdClientId, res.result.id]);
        },
        error: (err) => {
          this.utilities.fullPageLoading = false;
          this.popup.title = 'Failed';
          this.popup.content = err?.error?.[0]?.message ?? 'Failed to create onboarding';
          this.popup.warn = true;
          this.openDialog();
        }
      });
    });
  }
  createAcquirerOnbClient() {
    const dialogRef = this.dialog.open(UserFeedbackConfirmationComponent);

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.data !== GlobalVariables.CONFIRMED) return;

      this.utilities.fullPageLoading = true;
      this.createAcquirerOnboardingWizardService.createDemoAcquirerOnboarding().subscribe({
        next: (res: any) => {
          this.router.navigate(['acquire-onboarding', res.result.fdClientId, res.result.id]);
        },
        error: (err) => {
          this.utilities.fullPageLoading = false;
          this.popup.title = 'Failed';
          this.popup.content = err?.error?.[0]?.message ?? 'Failed to create onboarding';
          this.popup.warn = true;
          this.openDialog();
        }
      });
    });
  }

  openDialog(): void {
    const data = { ...this.popup, content: this.popup.content };
    this.utilities.showSnackbarAlert(data);
  }
}

